<template>
  <div class="license-plate-stats">
    <div class="header">
      <h2>车牌号统计查询</h2>
    </div>

    <!-- 查询表单 -->
    <el-form :model="queryForm" :inline="true" class="query-form">
      <el-form-item label="日期范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          :clearable="false"
          @change="handleDateChange"
        />
      </el-form-item>

      <el-form-item label="气站">
        <el-select
          v-model="queryForm.stationId"
          placeholder="全部气站"
          clearable
          filterable
          @change="handleQuery"
        >
          <el-option
            v-for="station in stations"
            :key="station.id"
            :label="station.name"
            :value="station.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="车牌号">
        <el-input
          v-model="queryForm.plateNumber"
          placeholder="请输入车牌号"
          clearable
          @keyup.enter.native="handleQuery"
        >
          <el-button
            slot="append"
            icon="el-icon-search"
            @click="handleQuery"
          />
        </el-input>
      </el-form-item>

      <el-form-item>
        <el-button
          type="primary"
          icon="el-icon-search"
          @click="handleQuery"
          :loading="loading"
        >
          查询
        </el-button>
        <el-button
          icon="el-icon-refresh"
          @click="handleReset"
        >
          重置
        </el-button>
        <el-button
          type="success"
          icon="el-icon-download"
          @click="handleExport"
          :loading="exporting"
        >
          导出
        </el-button>
      </el-form-item>
    </el-form>

    <!-- 统计概览 -->
    <el-row :gutter="20" class="stats-overview">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon total-count">
              <i class="el-icon-document"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.totalCount }}</div>
              <div class="stat-label">总加气次数</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon volume">
              <i class="el-icon-sold"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.totalVolume }}</div>
              <div class="stat-label">总加气量(m³)</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon amount">
              <i class="el-icon-money"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">¥{{ statsData.totalAmount }}</div>
              <div class="stat-label">总金额(元)</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon unique-plates">
              <i class="el-icon-truck"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.summary.uniquePlates }}</div>
              <div class="stat-label">不同车牌数</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 车牌号排行榜 -->
    <el-card shadow="hover" class="ranking-card">
      <div slot="header" class="card-header">
        <span>车牌号加气排行榜</span>
        <el-tag type="info">TOP 20</el-tag>
      </div>

      <el-table
        :data="statsData.topPlates"
        style="width: 100%"
        stripe
        border
        v-loading="loading"
      >
        <el-table-column
          type="index"
          label="排名"
          width="60"
          align="center"
        />
        <el-table-column
          prop="license_plate"
          label="车牌号"
          width="150"
          align="center"
        />
        <el-table-column
          prop="count"
          label="加气次数"
          width="100"
          align="center"
          sortable
        />
        <el-table-column
          prop="volume"
          label="加气量(m³)"
          width="120"
          align="center"
          sortable>
          <template slot-scope="scope">
            {{ scope.row.volume.toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="amount"
          label="金额(元)"
          width="120"
          align="center"
          sortable>
          <template slot-scope="scope">
            ¥{{ scope.row.amount.toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column
          label="平均加气量(m³)"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.count > 0 ? (scope.row.volume / scope.row.count).toFixed(2) : '0.00' }}
          </template>
        </el-table-column>
        <el-table-column
          label="平均消费(元)"
          align="center"
        >
          <template slot-scope="scope">
            ¥{{ scope.row.count > 0 ? (scope.row.amount / scope.row.count).toFixed(2) : '0.00' }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 日期趋势图 -->
    <el-card shadow="hover" class="trend-card">
      <div slot="header" class="card-header">
        <span>每日加气趋势</span>
        <el-button
          type="text"
          icon="el-icon-refresh"
          @click="handleQuery"
        />
      </div>

      <div id="daily-trend-chart" style="height: 400px"></div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getLicensePlateStats, exportLicensePlateStats } from '@/api/licensePlate'
import { fetchStations } from '@/api/station'

export default {
  name: 'LicensePlateStats',
  data() {
    return {
      loading: false,
      exporting: false,
      queryForm: {
        startDate: '',
        endDate: '',
        stationId: null,
        pageNumber: null
      },
      dateRange: [],
      stations: [],
      statsData: {
        totalCount: 0,
        totalVolume: 0,
        totalAmount: 0,
        topPlates: [],
        dailyStats: [],
        summary: {
          avgVolume: 0,
          avgAmount: 0,
          uniquePlates: 0
        }
      }
    }
  },
  mounted() {
    this.initDateRange()
    this.fetchStations()
    this.handleQuery()
  },
  methods: {
    // 初始化日期范围（最近30天）
    initDateRange() {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - 30)

      this.dateRange = [
        this.formatDate(startDate),
        this.formatDate(endDate)
      ]

      this.queryForm.startDate = this.dateRange[0]
      this.queryForm.endDate = this.dateRange[1]
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    handleDateChange(value) {
      if (value && value.length === 2) {
        this.queryForm.startDate = value[0]
        this.queryForm.endDate = value[1]
      }
    },

    // 获取气站列表
    async fetchStations() {
      try {
        const response = await fetchStations()
        if (response.code === 200) {
          this.stations = response.data || []
        }
      } catch (error) {
        console.error('获取气站列表失败:', error)
      }
    },

    // 查询统计数据
    async handleQuery() {
      this.loading = true
      try {
        const response = await getLicensePlateStats(this.queryForm)
        if (response.success) {
          this.statsData = response.data
          this.$nextTick(() => {
            this.initChart()
          })
        } else {
          this.$message.error('查询失败：' + (response.message || '未知错误'))
        }
      } catch (error) {
        console.error('查询统计数据失败:', error)
        this.$message.error('查询失败，请重试')
      } finally {
        this.loading = false
      }
    },

    // 重置查询
    handleReset() {
      this.initDateRange()
      this.queryForm.stationId = null
      this.queryForm.plateNumber = ''
      this.handleQuery()
    },

    // 导出数据
    async handleExport() {
      this.exporting = true
      try {
        const response = await exportLicensePlateStats(this.queryForm)

        // 创建Blob并下载
        const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `车牌号统计_${this.queryForm.startDate}_至_${this.queryForm.endDate}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败，请重试')
      } finally {
        this.exporting = false
      }
    },

    // 初始化图表
    initChart() {
      const chartDom = document.getElementById('daily-trend-chart')
      if (!chartDom) return

      const myChart = echarts.init(chartDom)

      const option = {
        title: {
          text: '每日加气趋势',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['加气次数', '加气量(m³)', '金额(元)'],
          top: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.statsData.dailyStats.map(item => item.date),
          axisLabel: {
            rotate: 45
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '加气次数',
            position: 'left'
          },
          {
            type: 'value',
            name: '加气量(m³)',
            position: 'right'
          },
          {
            type: 'value',
            name: '金额(元)',
            position: 'right',
            axisLabel: {
              formatter: '{value}'
            }
          }
        ],
        series: [
          {
            name: '加气次数',
            type: 'bar',
            data: this.statsData.dailyStats.map(item => item.count),
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '加气量',
            type: 'line',
            yAxisIndex: 1,
            data: this.statsData.dailyStats.map(item => item.volume.toFixed(2)),
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '金额',
            type: 'line',
            yAxisIndex: 2,
            data: this.statsData.dailyStats.map(item => item.amount.toFixed(2)),
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      }

      myChart.setOption(option)
      window.addEventListener('resize', () => {
        myChart.resize()
      })
    }
  }
}
</script>

<style scoped>
.license-plate-stats {
  padding: 20px;
}

.header {
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  color: #303133;
}

.query-form {
  margin-bottom: 20px;
  padding: 15px;
  background: #fff;
  border-radius: 4px;
}

.stats-overview {
  margin-bottom: 20px;
}

.stat-card {
  margin-bottom: 10px;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 10px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #fff;
}

.stat-icon.total-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-icon.volume {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-icon.amount {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-icon.unique-plates {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-content {
  flex: 1;
  margin-left: 15px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

.ranking-card,
.trend-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>